/**
 * Image with text — Animated header by Notch
 *
 * Origin is measured from the image column rectangle, then nudged toward content:
 *   centeredStart = imageLeft + imageWidth/2 - headingWidth/2
 *   inwardOffset  = imageWidth × (startOffset% / 100)
 *   Image first:  startX = centeredStart + inwardOffset
 *   Image second: startX = centeredStart - inwardOffset
 *
 * Scroll movement from that origin (unchanged direction):
 *   image_first  → currentX = startX + progress × distance
 *   text_first   → currentX = startX - progress × distance
 */
if (!customElements.get('iwt-animated-header')) {
  class IwtAnimatedHeader extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.image-with-text__animated-header-track');
      this.sectionRoot =
        this.closest('.image-with-text') ||
        this.closest('.shopify-section') ||
        this;

      this._startTranslateX = 0;
      this._raf = null;

      this._readSettings();
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onMotionChange = this._onMotionChange.bind(this);

      this._bind();
      this._scheduleMeasure();
    }

    disconnectedCallback() {
      this._unbind();
    }

    _readSettings() {
      this.scrollDistance = Number(this.dataset.scrollDistance) || 0;
      this.startOffsetPercent = Math.min(35, Math.max(0, Number(this.dataset.startOffset) || 0));
      this.disableMobile = this.dataset.disableMobile === 'true';
      // text_first = Desktop layout "Image second"
      this.scrollDirection = this.dataset.layout === 'text_first' ? -1 : 1;
    }

    _getImageColumn() {
      return this.sectionRoot.querySelector('.image-with-text__media-column');
    }

    _bind() {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });

      if (this.reducedMotionQuery.addEventListener) {
        this.reducedMotionQuery.addEventListener('change', this._onMotionChange);
      } else if (this.reducedMotionQuery.addListener) {
        this.reducedMotionQuery.addListener(this._onMotionChange);
      }

      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(() => this._scheduleMeasure());
        this._resizeObserver.observe(this.sectionRoot);
        const imageCol = this._getImageColumn();
        if (imageCol) this._resizeObserver.observe(imageCol);
        if (this.track) this._resizeObserver.observe(this.track);
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this._scheduleMeasure()).catch(() => {});
      }
    }

    _unbind() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);

      if (this.reducedMotionQuery.removeEventListener) {
        this.reducedMotionQuery.removeEventListener('change', this._onMotionChange);
      } else if (this.reducedMotionQuery.removeListener) {
        this.reducedMotionQuery.removeListener(this._onMotionChange);
      }

      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }

      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
      if (this._measureRaf) {
        cancelAnimationFrame(this._measureRaf);
        this._measureRaf = null;
      }
    }

    _onScroll() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this._applyTransform();
      });
    }

    _onResize() {
      this._scheduleMeasure();
    }

    _onMotionChange() {
      this._applyTransform();
    }

    _scheduleMeasure() {
      if (this._measureRaf) return;
      this._measureRaf = requestAnimationFrame(() => {
        this._measureRaf = null;
        this._measure();
        this._applyTransform();
      });
    }

    /**
     * Compute translateX for the heading origin (centered over image, then
     * offset toward content), relative to this host's layout position.
     */
    _measure() {
      if (!this.track) return;

      this._readSettings();

      const imageCol = this._getImageColumn();
      if (!imageCol) {
        this._startTranslateX = 0;
        this.setAttribute('data-ready', '');
        return;
      }

      // Clear transform so host/track geometry reflects the resting layout box.
      this.track.style.transform = 'translate3d(0, 0, 0)';

      const sectionRect = this.sectionRoot.getBoundingClientRect();
      const imageRect = imageCol.getBoundingClientRect();
      const hostRect = this.getBoundingClientRect();
      const headingWidth = this.track.offsetWidth;
      const viewWidth = window.innerWidth || document.documentElement.clientWidth;

      if (sectionRect.width <= 0 || imageRect.width <= 0 || headingWidth <= 0) {
        return;
      }

      const imageLeft = imageRect.left - sectionRect.left;
      const centeredStartLeft = imageLeft + imageRect.width / 2 - headingWidth / 2;
      const inwardOffset = imageRect.width * (this.startOffsetPercent / 100);
      // + toward content when image is left; − toward content when image is right
      let startLeft = centeredStartLeft + inwardOffset * this.scrollDirection;

      // Keep the resting heading inside the viewport
      const absoluteLeft = sectionRect.left + startLeft;
      const absoluteRight = absoluteLeft + headingWidth;
      if (absoluteLeft < 0) {
        startLeft -= absoluteLeft;
      } else if (absoluteRight > viewWidth) {
        startLeft -= absoluteRight - viewWidth;
      }

      const hostLeft = hostRect.left - sectionRect.left;
      this._startTranslateX = startLeft - hostLeft;
      this.setAttribute('data-ready', '');
    }

    _shouldAnimateScroll() {
      if (this.reducedMotionQuery.matches) return false;
      if (this.disableMobile && this.mobileQuery.matches) return false;
      if (this.scrollDistance === 0) return false;
      return true;
    }

    _progress() {
      const rect = this.sectionRoot.getBoundingClientRect();
      const viewHeight = window.innerHeight || document.documentElement.clientHeight;
      const total = viewHeight + rect.height;
      if (total <= 0) return 0;
      const raw = (viewHeight - rect.top) / total;
      return Math.min(1, Math.max(0, raw));
    }

    _applyTransform() {
      if (!this.track) return;

      this._readSettings();

      let currentX = this._startTranslateX;

      if (this._shouldAnimateScroll()) {
        currentX = this._startTranslateX + this._progress() * this.scrollDistance * this.scrollDirection;
      }

      this.track.style.transform = `translate3d(${currentX}px, 0, 0)`;
    }
  }

  customElements.define('iwt-animated-header', IwtAnimatedHeader);
}
